/** tm/components/GovFeedPanel.tsx — 공고 검토함 오른쪽 검토 패널 (2026-09-30)
 *
 *  머리(이동 ↑↓ · n/N · 원문 · 닫기) → AI 요약·근거 → 등록 조건(기존 GovProgramForm 그대로) → 하단 고정 버튼.
 *  폼 상태 · 자동 저장 · 승인/제외는 부모(GovFeedInbox)가 갖고, 이 컴포넌트는 그리기만 한다.
 */
import { useEffect, useMemo, useState } from 'react';
import { C, T, R, Badge, Btn } from './ui';
import { fmtDate } from '../hooks';
import GovProgramForm, { canSaveGovForm, type GovProgramFormState } from './GovProgramForm';
import { GovFeedAiBox, ScreenBadge, FeedDeadline, AMBER, unsureLabels, evidenceQuote, PAST_NOTE, SOURCE_LABEL } from './GovFeedParts';
/** 같은 공고로 묶은 근거 */
const SAME_WHY: Record<string, string> = { file: '공고문 같음', name: '공고문 이름 같음', title: '사업명·마감 같음' };
import type { GovFeedDetail } from '../types';
import { StarIcon } from './GovFeedTable';

export type DraftSaveState = 'idle' | 'saving' | 'saved' | 'error';
export type PanelBusy = null | 'approve' | 'exclude' | 'restore' | 'analyze' | 'saveReg';

const linkBtn: React.CSSProperties = { padding: '3px 8px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', fontSize: T.xs, color: C.sub, textDecoration: 'none', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 3 };
const iconBtn: React.CSSProperties = { width: 26, height: 26, borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', color: C.sub, fontSize: T.sm, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 };
const kbd: React.CSSProperties = { display: 'inline-block', minWidth: 16, padding: '0 4px', marginLeft: 6, borderRadius: 4, border: '1px solid rgba(255,255,255,0.45)', fontSize: 10, fontWeight: 700, lineHeight: '16px', textAlign: 'center' };
const kbdDk: React.CSSProperties = { ...kbd, border: `1px solid ${C.borderDk}`, color: C.sub };

const SAVE_TEXT: Record<DraftSaveState, string> = { idle: '', saving: '저장 중…', saved: '자동 저장됨', error: '자동 저장 실패' };

interface Props {
  detail: GovFeedDetail | null;
  loading: boolean;
  form: GovProgramFormState;
  setForm: React.Dispatch<React.SetStateAction<GovProgramFormState>>;
  applyMode: 'update' | 'new';
  setApplyMode: (m: 'update' | 'new') => void;
  saveState: DraftSaveState;
  busy: PanelBusy;
  aiConfigured: boolean;
  index: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  onApprove: () => void;
  /** 승인 완료 공고 — 폼(등록된 공고 값)을 등록된 공고에 저장 (2026-10-10 지도사 「리포트에서 눌러 들어가 바꿨는데 저장 버튼이 없어」) · regName = 등록된 공고 이름 · 없으면 버튼 없음 */
  onSaveRegistered?: () => void;
  regName?: string | null;
  onExclude: () => void;
  onRestore: () => void;
  /** batch = 일괄(50% 할인 · 보통 1시간) · now = 지금 바로(정가) */
  onAnalyze: (mode?: 'batch' | 'now') => void;
  /** 근거 → 원문 형광 */
  onQuote?: (quote: string, label: string) => void;
  /** 집중 모드 중 — 이동·위치·닫기는 위 띠가 맡으므로 머리에서 뺀다 */
  focusMode?: boolean;
  /** 「집중 모드」 켜기 (F) */
  onFocusMode?: () => void;
  /** 관심 별표 (S) — 2026-10-03 */
  onStar?: (on: boolean) => void;
  /** 같은 공고 묶음에서 하나 빼기 (따로 보기) */
  onSplit?: (childId: string) => void;
}

/** 네 모서리로 펼치는 모양 — 집중 모드 단추 */
export function FocusIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 4V1h3M8 1h3v3M11 8v3H8M4 11H1V8" />
    </svg>
  );
}

export default function GovFeedPanel(p: Props) {
  const { detail, loading, form, setForm, applyMode, setApplyMode, saveState, busy, aiConfigured } = p;
  const [evOpen, setEvOpen] = useState(false);
  const unsure = useMemo(() => unsureLabels(detail?.ai ?? null), [detail]);
  const g = detail?.group;
  // 근거는 확인할 칸이 있을 때만 펼쳐 둔다 — 나머지는 폼이 바로 보이게
  const gid = g?.id;
  const hasUnsure = unsure.length > 0;
  useEffect(() => { setEvOpen(hasUnsure); }, [gid, hasUnsure]);

  // 일정 토글 — 폼 값으로 지금 상태를 판단(따로 상태를 두지 않아 칸을 직접 고쳐도 어긋나지 않는다)
  const next = detail ? { apply_start: detail.prefill.apply_start ?? null, deadline: detail.prefill.deadline ?? null } : null;
  const orig = detail?.prefillOrig ?? null;
  const same = (d: { apply_start: string | null; deadline: string | null } | null) =>
    !!d && (form.apply_start || '') === (d.apply_start || '').slice(0, 10) && (form.first_come || (form.deadline || '') === (d.deadline || '').slice(0, 10));
  const dateMode: 'next' | 'orig' | null = same(orig) ? 'orig' : same(next) ? 'next' : null;
  const setDates = (d: { apply_start: string | null; deadline: string | null } | null) => {
    if (!d) return;
    setForm(f => ({ ...f, apply_start: (d.apply_start || '').slice(0, 10), deadline: f.first_come ? '' : (d.deadline || '').slice(0, 10) }));
  };

  if (!detail || !g) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: 35, boxSizing: 'border-box', padding: '0 16px', borderBottom: `1px solid ${C.border}` }}>
          {!p.focusMode && <button onClick={p.onClose} style={iconBtn} aria-label="닫기">✕</button>}
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted, fontSize: T.md }}>{loading ? '불러오는 중…' : '공고를 불러오지 못했습니다'}</div>
      </div>
    );
  }

  const dup = detail.dupProgram;
  const aiDone = !!(detail.ai && g.aiStatus === 'done');
  const first = detail.items[0];
  const isNew = g.review === 'new';

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
      {/* 머리 — 이동 · 위치 · 저장 상태 · 원문 · 닫기 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: 35, boxSizing: 'border-box', padding: '0 16px', borderBottom: `1px solid ${C.border}`, background: C.bgAlt, flexShrink: 0 }}>
        {p.focusMode ? <span style={{ fontSize: T.sm, fontWeight: 700, color: C.text }}>검토·등록</span> : (
          <>
            <button onClick={p.onPrev} disabled={p.index <= 0} style={{ ...iconBtn, opacity: p.index <= 0 ? 0.4 : 1 }} aria-label="이전 공고" title="이전 (↑)">↑</button>
            <button onClick={p.onNext} disabled={p.index >= p.total - 1} style={{ ...iconBtn, opacity: p.index >= p.total - 1 ? 0.4 : 1 }} aria-label="다음 공고" title="다음 (↓)">↓</button>
            <span style={{ fontSize: T.xs, color: C.muted, fontVariantNumeric: 'tabular-nums', marginLeft: 2 }}>{p.index + 1} / {p.total.toLocaleString()}</span>
          </>
        )}
        {p.onStar && (
          <button onClick={e => { e.currentTarget.blur(); p.onStar!(!g.starred); }} aria-pressed={!!g.starred} title={g.starred ? '관심 해제 (S)' : '관심 표시 — 「★ 관심」에서 모아 보기 (S)'}
            style={{ ...linkBtn, height: 26, boxSizing: 'border-box', gap: 5, marginLeft: p.focusMode ? 6 : 4,
              ...(g.starred ? { borderColor: '#FCD34D', background: '#FFFBEB', color: '#B45309', fontWeight: 600 } : { color: C.sub }) }}>
            <span style={{ color: g.starred ? '#F59E0B' : C.borderDk, display: 'inline-flex' }}><StarIcon on={!!g.starred} size={13} /></span>
            {g.starred ? '관심' : '관심 표시'}<span style={{ ...kbdDk, marginLeft: 1 }}>S</span>
          </button>
        )}
        <span style={{ flex: 1 }} />
        {saveState !== 'idle' && (
          <span style={{ fontSize: T.xs, color: saveState === 'error' ? C.red : C.muted, whiteSpace: 'nowrap' }}>
            {saveState === 'saved' ? '✓ ' : ''}{SAVE_TEXT[saveState]}
          </span>
        )}
        {first?.url && <a href={first.url} target="_blank" rel="noopener noreferrer" style={{ ...linkBtn, height: 26, boxSizing: 'border-box' }}>원문 ↗</a>}
        {!p.focusMode && p.onFocusMode && (
          <button onClick={e => { e.currentTarget.blur(); p.onFocusMode!(); }} title="원문과 등록 양식만 크게 (F)" aria-label="집중 모드 켜기"
            style={{ ...linkBtn, height: 26, boxSizing: 'border-box', gap: 5, color: C.text, fontWeight: 600 }}>
            <FocusIcon />집중 모드<span style={{ ...kbdDk, marginLeft: 1 }}>F</span>
          </button>
        )}
        {!p.focusMode && <button onClick={p.onClose} style={iconBtn} aria-label="닫기" title="닫기 (Esc)">✕</button>}
      </div>

      <div key={g.id} style={{ flex: 1, overflowY: 'auto', padding: '16px 20px 24px', opacity: loading ? 0.6 : 1, transition: 'opacity 0.12s' }}>
        <div style={{ maxWidth: 760 }}>
          {/* 공고 */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: T.md, fontWeight: 700, color: C.text, lineHeight: 1.45 }}>{first?.title || g.title}</div>
            <div style={{ fontSize: T.sm, color: C.sub, marginTop: 4 }}>
              {[g.ministry, g.agency && g.agency !== g.ministry ? g.agency : null, g.field].filter(Boolean).join(' · ')}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4, marginTop: 10 }}>
              <span style={{ fontSize: T.xs, fontWeight: 600, color: C.sub, marginRight: 2 }}>회차</span>
              {detail.items.slice(0, 6).map((it, i) => {
                const ym = (it.registeredAt || it.applyStart || '').slice(0, 7).replace('-', '.');
                const period = it.applyEnd ? `${fmtDate(it.applyStart)} ~ ${fmtDate(it.applyEnd)}` : (it.applyText || '');
                return (
                  <a key={it.pblancId} href={it.url} target="_blank" rel="noopener noreferrer" title={`${it.title}\n${period}`}
                    style={{ ...linkBtn, padding: '2px 8px', fontWeight: i === 0 ? 600 : 400, color: i === 0 ? C.text : C.sub, background: i === 0 ? C.white : C.bgAlt }}>
                    {ym || it.pblancId} ↗
                  </a>
                );
              })}
              {detail.items.length > 6 && <span style={{ fontSize: T.xs, color: C.muted }}>+{detail.items.length - 6}</span>}
              <span style={{ marginLeft: 4 }}><FeedDeadline end={g.latestEnd} years={g.years} /></span>
            </div>
            {/* 같은 공고 — 다른 채널(네이버·소상공인24·지자체)에서 들어온 같은 공고를 이 공고에 묶었다 (2026-10-03) */}
            {!!detail.sameNotices?.length && (
              <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: R.sm, background: C.bgAlt, border: `1px solid ${C.border}` }}>
                <div style={{ fontSize: T.xs, fontWeight: 700, color: C.sub, marginBottom: 4 }}>
                  같은 공고 {detail.sameNotices.length}건 더 <span style={{ fontWeight: 400, color: C.muted }}>— 다른 채널에서 들어와 이 공고로 묶었습니다 · 등록·제외는 이 공고 하나로</span>
                </div>
                {detail.sameNotices.map(d => (
                  <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 26, fontSize: T.xs }}>
                    <span style={{ flexShrink: 0, padding: '0 6px', borderRadius: 4, background: C.white, border: `1px solid ${C.border}`, color: C.sub, lineHeight: '18px' }}>{SOURCE_LABEL[d.source || ''] || d.source || '출처'}</span>
                    <span title={`${d.title}${d.agency ? `\n${d.agency}` : ''}`} style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: C.text }}>{d.title}</span>
                    <span title="묶은 근거" style={{ flexShrink: 0, color: C.muted }}>{SAME_WHY[d.reason || ''] || ''}</span>
                    {d.url && <a href={d.url} target="_blank" rel="noopener noreferrer" style={{ ...linkBtn, padding: '1px 7px', flexShrink: 0 }}>원문 ↗</a>}
                    {p.onSplit && isNew && <button onClick={() => p.onSplit!(d.id)} title="같은 공고가 아니면 — 따로 목록에 올립니다(다시 묶지 않음)" style={{ ...linkBtn, padding: '1px 7px', flexShrink: 0, color: C.sub }}>따로 보기</button>}
                  </div>
                ))}
              </div>
            )}
            {!isNew && (
              <div style={{ fontSize: T.sm, marginTop: 8, fontWeight: 600, color: g.review === 'approved' ? C.green : C.sub }}>
                {g.review === 'approved' ? '승인됨' : `제외됨${g.excludeReason ? ` · ${g.excludeReason}` : ''}`}
                {g.reviewedBy ? ` · ${g.reviewedBy}` : ''}{g.reviewedAt ? ` · ${fmtDate(g.reviewedAt)}` : ''}
              </div>
            )}
          </div>

          {/* AI 요약 */}
          {aiDone ? (
            <GovFeedAiBox ai={detail.ai!} screen={g.screen} open={evOpen} onToggle={() => setEvOpen(o => !o)} onQuote={p.onQuote} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: R.md, border: `1px dashed ${g.aiStatus === 'error' ? `${C.red}66` : C.borderDk}`, background: g.aiStatus === 'error' ? C.redLt : C.bgAlt }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ScreenBadge screen={g.screen} />
                  <span style={{ fontSize: T.sm, fontWeight: 700, color: g.aiStatus === 'error' ? C.red : C.text }}>
                    {busy === 'analyze' && g.aiStatus === 'running' ? 'AI가 공고문을 읽는 중… (최대 1분 30초)'
                      : g.aiStatus === 'error' ? 'AI 분석 실패'
                        : g.aiStatus === 'running' ? 'AI 분석 중'
                          : g.aiStatus === 'batched' ? '분석 대기(일괄)'
                          : g.aiStatus === 'skipped' ? 'AI 분석 안 함 (비추천)'
                          : g.aiStatus === 'past' ? '지난 공고 — 자동 분석 안 함' : '분석 대기 중'}
                  </span>
                </div>
                <div style={{ fontSize: T.xs, color: C.sub, marginTop: 5, lineHeight: 1.5 }}>
                  {g.aiStatus === 'batched' ? '일괄 처리 중(50% 할인) — 보통 1시간 안에(최대 24시간) 결과 · 급하면 「지금 바로」(정가)'
                    : g.aiStatus === 'queued' && g.starred ? '심층 분석 요청됨 — 곧 일괄로 보내고, 보통 1시간 안에 결과 · 급하면 「지금 바로」(정가)'
                    : g.aiStatus === 'queued' || g.aiStatus === 'none' ? '아직 분석하지 않았습니다 — 「일괄 분석」(또는 ☆)을 누르면 이 공고만 심층 분석' : g.aiStatus === 'past' ? PAST_NOTE : g.aiStatus === 'error' && g.aiError ? g.aiError : g.screenReason ? `선별 메모: ${g.screenReason}` : '지금은 목록 정보로만 칸을 채웠습니다'}
                </div>
              </div>
              {g.review !== 'approved' && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 4, flexShrink: 0 }}>
                  {g.aiStatus !== 'batched' && g.aiStatus !== 'running' && !(g.aiStatus === 'queued' && g.starred) && (
                    <Btn variant="secondary" onClick={() => p.onAnalyze('batch')} disabled={!!busy || !aiConfigured} title="일괄(Batch)로 분석 — 50% 할인 · 보통 1시간 안에 결과"
                      style={{ fontSize: T.xs, padding: '6px 10px', whiteSpace: 'nowrap', color: C.accent }}>
                      {busy === 'analyze' ? '넣는 중…' : '일괄 분석 · 50%↓'}
                    </Btn>
                  )}
                  <button type="button" onClick={() => p.onAnalyze('now')} disabled={!!busy || !aiConfigured} title="지금 바로 분석 — 정가 · 1분 안팎"
                    style={{ border: 'none', background: 'transparent', padding: '2px 4px', fontSize: T.xs, color: C.sub, textDecoration: 'underline', cursor: busy || !aiConfigured ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
                    지금 바로 (정가)
                  </button>
                </div>
              )}
            </div>
          )}

          {/* 등록 조건 */}
          <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 20, paddingTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: T.md, fontWeight: 700, color: C.text }}>등록 조건</span>
              <span style={{ fontSize: T.xs, color: C.muted }}>{aiDone ? 'AI가 채운 값 — 틀린 곳만 눌러 고치세요' : '목록 정보로 채운 값 — 확인 후 고치세요'}</span>
            </div>

            {unsure.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4, padding: '8px 12px', marginBottom: 10, borderRadius: R.md, background: AMBER.bg, border: `1px solid ${AMBER.border}` }}>
                <span style={{ fontSize: T.sm, fontWeight: 700, color: AMBER.text, marginRight: 4 }}>확인 필요</span>
                {unsure.map(l => {
                  const q = p.onQuote ? evidenceQuote(detail.ai, l) : '';
                  return q
                    ? <button key={l} onClick={() => p.onQuote!(q, l)} title="원문에서 근거 보기"
                      style={{ padding: '2px 8px', borderRadius: R.sm, border: `1px solid ${AMBER.border}`, background: C.white, color: AMBER.text, fontSize: T.xs, fontWeight: 600, cursor: 'pointer' }}>{l} ↗</button>
                    : <Badge key={l} bg={C.white} color={AMBER.text} style={{ border: `1px solid ${AMBER.border}`, padding: '2px 8px' }}>{l}</Badge>;
                })}
              </div>
            )}

            {detail.prefillMode === 'nextYear' && orig && (
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: T.xs, fontWeight: 600, color: C.sub }}>일정</span>
                <div style={{ display: 'inline-flex', padding: 2, gap: 2, borderRadius: 8, background: C.bg }}>
                  {([['next', `내년 예상 (+${detail.prefillShiftYears || 1}년)`, next], ['orig', '이번 공고 날짜', orig]] as ['next' | 'orig', string, typeof orig][]).map(([k, label, d]) => (
                    <button key={k} onClick={() => setDates(d)} style={{
                      padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: T.xs,
                      background: dateMode === k ? C.white : 'transparent', color: dateMode === k ? C.text : C.sub,
                      fontWeight: dateMode === k ? 700 : 500, boxShadow: dateMode === k ? '0 1px 2px rgba(17,24,39,0.10)' : 'none',
                    }}>{label}</button>
                  ))}
                </div>
                <span style={{ fontSize: T.xs, color: C.muted, fontVariantNumeric: 'tabular-nums' }}>
                  이번 공고 {orig.apply_start ? `${fmtDate(orig.apply_start)} ~ ` : ''}{fmtDate(orig.deadline) || '상시'}
                </span>
              </div>
            )}

            {dup && isNew && (
              <div style={{ padding: '10px 12px', marginBottom: 12, borderRadius: R.md, background: C.accentLt, border: '1px solid #BFDBFE' }}>
                <div style={{ fontSize: T.sm, color: C.text, lineHeight: 1.5 }}>
                  <b>이미 등록된 공고가 있습니다</b> — 「{dup.name}」{dup.deadline ? ` · 마감 ${fmtDate(dup.deadline)}` : ''}{dup.isActive === false ? ' · 비활성' : ''}
                </div>
                <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                  {([['update', '기존 공고 갱신'], ['new', '새로 등록']] as ['update' | 'new', string][]).map(([k, label]) => (
                    <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: T.sm, fontWeight: applyMode === k ? 700 : 400, color: applyMode === k ? C.accent : C.sub }}>
                      <input type="radio" name="gf-apply-mode" checked={applyMode === k} onChange={() => setApplyMode(k)} style={{ accentColor: C.accent }} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <GovProgramForm key={g.id} form={form} setForm={setForm} sourceTitle={first?.title || g.title} />
          </div>
        </div>
      </div>

      {/* 하단 고정 버튼 */}
      <div style={{ flexShrink: 0, borderTop: `1px solid ${C.border}`, background: C.white, padding: '10px 20px' }}>
        <div style={{ maxWidth: 760, display: 'flex', alignItems: 'center', gap: 8 }}>
          {g.review === 'approved' ? (
            p.onSaveRegistered && p.regName != null ? (
              <>
                <div style={{ flex: 1, minWidth: 0, fontSize: T.sm, color: C.green, fontWeight: 600, lineHeight: 1.4 }}>
                  승인 완료 · 위 칸은 등록된 공고 「{p.regName}」 값입니다
                  <div style={{ fontSize: T.xs, color: C.muted, fontWeight: 500 }}>고치고 저장하면 매칭 · fphub 리포트에 바로(5분 안) 반영됩니다</div>
                </div>
                <Btn onClick={p.onSaveRegistered} disabled={!!busy || !canSaveGovForm(form)} style={{ flex: '0 0 auto', whiteSpace: 'nowrap', padding: '8px 18px' }}>
                  {busy === 'saveReg' ? '저장 중…' : '등록된 공고에 저장'}
                </Btn>
              </>
            ) : (
              <div style={{ flex: 1, fontSize: T.sm, color: C.green, fontWeight: 600 }}>승인 완료 — 「등록된 공고」에서 고칠 수 있습니다{dup ? ` (${dup.name})` : ''}</div>
            )
          ) : (
            <>
              {/* 다시 분석 — 왼쪽 = 일괄(50% 할인, 기본) · 오른쪽 「지금」 = 바로(정가) */}
              <div role="group" aria-label="다시 분석" style={{ display: 'inline-flex', alignSelf: 'stretch', flex: '0 0 auto', opacity: busy || !aiConfigured ? 0.5 : 1, borderRadius: R.md, border: `1px solid ${C.border}`, overflow: 'hidden', background: C.white }}>
                <button type="button" onClick={() => p.onAnalyze('batch')} disabled={!!busy || !aiConfigured} title="일괄(Batch)로 다시 분석 — 50% 할인 · 보통 1시간 안에 결과"
                  style={{ border: 'none', background: 'transparent', padding: '0 12px', fontSize: T.sm, fontWeight: 600, color: C.text, cursor: busy || !aiConfigured ? 'default' : 'pointer', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  {busy === 'analyze' ? '처리 중…' : <>다시 분석<span style={{ fontSize: 10.5, fontWeight: 700, color: C.green, background: '#ECFDF5', borderRadius: 4, padding: '0 4px', lineHeight: '16px' }}>50%↓</span></>}
                </button>
                <button type="button" onClick={() => p.onAnalyze('now')} disabled={!!busy || !aiConfigured} title="지금 바로 다시 분석 — 정가 · 1분 안팎"
                  style={{ border: 'none', borderLeft: `1px solid ${C.border}`, background: C.bgAlt, padding: '0 10px', fontSize: T.xs, fontWeight: 600, color: C.sub, cursor: busy || !aiConfigured ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
                  지금 바로
                </button>
              </div>
              {g.review === 'excluded'
                ? <Btn variant="secondary" onClick={p.onRestore} disabled={!!busy} style={{ flex: 1, color: C.green, whiteSpace: 'nowrap' }}>{busy === 'restore' ? '처리 중…' : '검토 대기로 복원'}</Btn>
                : <Btn variant="danger" onClick={p.onExclude} disabled={!!busy} style={{ flex: 1, whiteSpace: 'nowrap' }}>{busy === 'exclude' ? '처리 중…' : <>제외하고 다음<span style={{ ...kbdDk, color: C.red, borderColor: `${C.red}55` }}>X</span></>}</Btn>}
              {isNew && (
                <Btn onClick={p.onApprove} disabled={!!busy || !canSaveGovForm(form)} style={{ flex: 2, whiteSpace: 'nowrap' }}>
                  {busy === 'approve' ? '저장 중…' : <>{dup && applyMode === 'update' ? '갱신하고 다음' : '등록하고 다음'}<span style={kbd}>⏎</span></>}
                </Btn>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
